import React, { useEffect } from 'react'
import { ReactComponent as IconSort } from '../../../assets/images/icon-sort.svg'
import { ReactComponent as ArrowRight } from '../../../assets/images/icon-arrow-right.svg'
import { ReactComponent as ArrowLeft } from '../../../assets/images/icon-arrow-left.svg'
import { useNavigate } from 'react-router-dom'

import useCustomStyles from '../../../hooks/useCustomStyles'
import { tableStyles } from '../../../assets/styles/ClientsStyles/tableStyles'
import { useSelector, useDispatch } from 'react-redux'
import { selectState } from '../../../store/slices/themeSlice'
import FilterBar from './searchBar'
import {
  FinancialInstitutionSearchResultsResponse,
  FinancialInstitutionsType,
  searchFinancialInstitutions,
} from '../../../api/awsApis'
import debounce from 'lodash.debounce'
import {
  setCurrentData,
  setCurrentPage,
  setLoading,
  setFilterType,
  setSearchValue,
} from '../../../store/slices/fiSlice'
import ColumnInfo from '../../../components/ColumnInfo'
type FIKey = keyof FinancialInstitutionsType
interface TableColumn {
  key: FIKey
  label: string
  /** Shown in an ⓘ tooltip next to the header label. */
  description?: string
}

interface TableProps {
  columns: TableColumn[]
  data: FinancialInstitutionsType[]
  onRowClick?: (row: FinancialInstitutionsType) => void
}

const TableWithSearch: React.FC<TableProps> = ({
  columns,
  data,
  onRowClick,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { theme } = useSelector(selectState)
  // Track sort direction in component state
  const [sortConfig, setSortConfig] = React.useState<{
    key: FIKey
    direction: 'asc' | 'desc'
  } | null>(null)

  //fiSlice state
  const fiState = useSelector((state: any) => state.fi_slice)
  const classes = useCustomStyles(tableStyles, theme)
  // const [filterType, setFilterType] = useState('Country')
  const setFilterTypeF = (filter: 'Country' | 'Financial Institution') => {
    dispatch(setFilterType(filter))
  }

  const setSearchValueF = (value: string) => {
    // dispatch(setCurrentPage(1))
    dispatch(setSearchValue(value))
  }
  // const [searchValue, setSearchValue] = useState('')
  // const [loading, setLoading] = useState(false)
  // const [currentPage, setCurrentPage] = useState(1)
  // const [currentData, setCurrentData] = useState<FinancialInstitutionsType[]>(
  //   data,
  // )

  // Apply filtering when searchValue or filterType changes

  useEffect(() => {
    //dispatch data to fislice
    if (!fiState.currentData.length) dispatch(setCurrentData(data))

    if (!fiState.searchValue) return

    dispatch(setLoading(true))
    console.log(
      'Filter Type:',
      fiState.filterType,
      'Search Value:',
      fiState.searchValue,
    )

    const debouncedSearch = debounce(async () => {
      console.log('starting debounced search')
      if (
        (fiState.filterType == 'Financial Institution' &&
          fiState.searchValue.length >= 3) ||
        (fiState.filterType == 'Country' && fiState.searchValue.length >= 2)
      ) {
        const searchType =
          fiState.filterType == 'Financial Institution' ? 'fi' : 'country'
        // uppercase if country
        const formattedSearchValue =
          fiState.filterType == 'Country'
            ? fiState.searchValue.toUpperCase()
            : fiState.searchValue
        try {
          const apiCall: FinancialInstitutionSearchResultsResponse = await searchFinancialInstitutions(
            formattedSearchValue,
            searchType,
          )
          dispatch(setLoading(false))
          dispatch(setCurrentData(apiCall.response))
          dispatch(setCurrentPage(1))
        } catch (err) {
          console.error('Search failed:', err)
        } finally {
          dispatch(setLoading(false))
        }
      } else {
        console.log(
          'not yet meeting requirements for search',
          fiState.filterType,
          fiState.searchValue,
        )
      }
    }, 400)

    debouncedSearch()
    console.log('Current Data:', fiState.currentData)

    return () => {
      debouncedSearch.cancel()
    }
  }, [fiState.filterType, fiState.searchValue, data])

  // Pagination logic
  const totalPages = 1

  const paginate = (pageNumber: number) => dispatch(setCurrentPage(pageNumber))

  const filterOptions = [
    { key: 'country', value: 'Country' },
    { key: 'providerName', value: 'Financial Institution' },
  ]

  const handleRowClick = (rowData: FinancialInstitutionsType) => {
    // If an onRowClick prop was provided, use that
    if (onRowClick) {
      console.log(rowData)
      onRowClick(rowData)
    } else {
      // Otherwise, use default navigation behavior
      console.log(rowData)

      navigate('/financialInstitutionsDetails', { state: { rowData } })
    }
  }

  const handleSort = (key: FIKey) => {
    // Implement sorting logic here if needed
    console.log(`Sorting by ${key}`)

    // Implement sorting logic here if needed

    const sortedData = [...fiState.currentData]
    if (sortConfig) {
      sortedData.sort((a, b) => {
        let result = 0
        if (
          typeof a[sortConfig.key] === 'string' &&
          typeof b[sortConfig.key] === 'string'
        ) {
          result = a[sortConfig.key].localeCompare(b[sortConfig.key])
        } else if (
          typeof a[sortConfig.key] === 'number' &&
          typeof b[sortConfig.key] === 'number'
        ) {
          result = a[sortConfig.key] - b[sortConfig.key]
        }
        return sortConfig.direction === 'asc' ? result : -result
      })
    }

    // Update sort direction on click
    if (sortConfig?.key === key) {
      setSortConfig({
        key,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
      })
    } else {
      setSortConfig({ key, direction: 'asc' })
    }

    dispatch(setCurrentData(sortedData))
    console.log('Sorted Data:', sortedData)
  }

  return (
    <div className={classes.container}>
      <div className={classes.filterBar}>
        <FilterBar
          filterType={fiState.filterType}
          searchValue={fiState.searchValue}
          setFilterType={setFilterTypeF}
          setSearchValue={setSearchValueF}
          filterOptions={filterOptions}
        />
      </div>
      {fiState.loading && (
        <div className={classes.loadingContainer}>
          <span className={classes.loadingText}>Loading...</span>
        </div>
      )}
      <div className={classes.tableWrapper}>
      <table className={classes.table}>
        <thead>
          <tr className={classes.tableHeader}>
            {columns.map(({ key, label, description }) => (
              <th
                key={key}
                onClick={() => handleSort(key)}
                style={{
                  width: '20%',
                  cursor: 'pointer',
                  padding: '15px',
                  fontSize: 16,
                  fontFamily: 'Gilroy-SemiBold',
                }}
              >
                <div className={classes.headerContent}>
                  <span className={classes.headerLabel}>{label}</span>
                  {description && <ColumnInfo label={label} description={description} />}
                  <IconSort width={20} height={20} />
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {fiState.currentData.map(
            (item: FinancialInstitutionsType, idx: number) => {
              return (
                <tr
                  key={idx}
                  className={classes.tableRow}
                  onClick={() => handleRowClick(item)}
                >
                  {/* Display regular columns */}
                  {columns.map(({ key }) => {
                    const rawValue =
                      key === 'updatedAt'
                        ? item[key]?.split?.('T')[0]
                        : item[key]

                    let displayValue: React.ReactNode
                    if (
                      typeof rawValue === 'string' ||
                      typeof rawValue === 'number'
                    ) {
                      displayValue = rawValue
                    } else if (Array.isArray(rawValue)) {
                      displayValue = rawValue
                        .map((v, i) =>
                          typeof v === 'object' ? JSON.stringify(v) : String(v),
                        )
                        .join(', ')
                    } else if (
                      typeof rawValue === 'object' &&
                      rawValue !== null
                    ) {
                      displayValue = JSON.stringify(rawValue)
                    } else if (rawValue === undefined || rawValue === null) {
                      displayValue = ''
                    } else {
                      displayValue = String(rawValue)
                    }

                    return (
                      <td key={key} className={`${classes.tableCell}`}>
                        {displayValue}
                      </td>
                    )
                  })}

                  {/* Action cell with arrow */}
                  <td className={`${classes.tableCell} ${classes.actionCell}`}>
                    <ArrowRight width={15} height={15} />
                  </td>
                </tr>
              )
            },
          )}
        </tbody>
      </table>
      </div>
      {/* Pagination Component */}
      <div className={classes.paginationContainer}>
        {fiState.currentPage > 1 && (
          <button
            onClick={() => paginate(fiState.currentPage - 1)}
            disabled={fiState.currentPage === 1}
            className={classes.paginationButton}
          >
            <ArrowLeft width={15} height={15} />
          </button>
        )}
        <span className={classes.pageInfo}>
          Page {fiState.currentPage} of {totalPages}
        </span>
        {fiState.currentPage < totalPages && (
          <button
            onClick={() => paginate(fiState.currentPage + 1)}
            disabled={fiState.currentPage === totalPages}
            className={classes.paginationButton}
          >
            <ArrowRight width={15} height={15} />
          </button>
        )}
      </div>
    </div>
  )
}

export default TableWithSearch
